import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axios from 'axios'

interface Company {
  id: number
  name: string
  message?: string
  logo_url?: string
  subscription_type: string
  on_trial: boolean
  trial_expired: boolean
  trial_days_remaining: number
  trial_expires_at: string | null
  requires_upgrade: boolean
}

export const useCompanyStore = defineStore('company', () => {
  const company = ref<Company | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const fetchCompany = async (companyId?: number): Promise<Company | null> => {
    try {
      isLoading.value = true
      error.value = null

      const url = companyId ? `/api/companies/${companyId}` : '/api/companies'
      const response = await axios.get(url)

      if (response.data?.company) {
        company.value = response.data.company
        return company.value
      }

      throw new Error('No company data received')
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to fetch company data'
      return null
    } finally {
      isLoading.value = false
    }
  }

  const updateCompany = async (updates: Partial<Company>): Promise<Company | null> => {
    if (!company.value) return null

    try {
      isLoading.value = true
      error.value = null

      const response = await axios.put(`/api/companies/${company.value.id}`, {
        company: updates
      })

      if (response.data?.company) {
        company.value = response.data.company
        return company.value
      }

      throw new Error('Failed to update company')
    } catch (err: any) {
      error.value = err.response?.data?.error || 'Failed to update company'
      return null
    } finally {
      isLoading.value = false
    }
  }

  const hasTrialExpired = computed(() => {
    return company.value?.requires_upgrade === true
  })

  const trialStatus = computed(() => {
    if (!company.value) return null

    if (company.value.subscription_type !== 'trial') {
      return 'paid'
    }

    if (company.value.trial_expired) {
      return 'expired'
    }

    return 'active'
  })

  const trialWarning = computed(() => {
    if (!company.value || !company.value.on_trial) return null
    
    const daysRemaining = company.value.trial_days_remaining
    
    if (daysRemaining <= 3) {
      return {
        type: 'urgent',
        message: `Your trial expires in ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''}!`,
        daysRemaining
      }
    }
    
    if (daysRemaining <= 7) {
      return {
        type: 'warning', 
        message: `${daysRemaining} days remaining in your trial`,
        daysRemaining
      }
    }
    
    return null
  })

  return {
    company,
    isLoading,
    error,
    hasTrialExpired,
    trialStatus,
    trialWarning,
    fetchCompany,
    updateCompany
  }
})